import React, { useMemo } from 'react';
import type ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { renderToStaticMarkup } from 'react-dom/server';
import SimpleBar from 'simplebar-react';
import type { VisibilityReportTopicShare } from '@brand24-original/llm-types';
import ReportSection from './ReportSection';
import CustomBarChart from '../chart/custom-bar-chart/CustomBarChart';
import ChartTooltip from '../chart/custom-bar-chart/components/chart-tooltip/ChartTooltip';
import { prepareAndGetVerticalBarOptions } from '../chart/custom-bar-chart/helpers/CustomBarChartHelper';
import { useChartColorPalette } from '../chart/custom-bar-chart/hooks/UseChartColorPalette';
import { useLegendState } from '../chart/custom-bar-chart/hooks/UseLegendState';
import './ReportTopicShare.scss';

/** Szerokość jednego słupka — tak samo jak na zakładce Context, żeby wykres wyglądał identycznie. */
const BAR_WIDTH = 150;

interface ReportTopicShareProps {
  topics: VisibilityReportTopicShare[];
  /** Raport zrzuca z tej instancji obraz wykresu do PDF-a. */
  chartRef?: React.RefObject<ReactECharts>;
}

/**
 * Które tematy generują rozmowy, w których marka bierze udział. To ten sam wykres, co sekcja
 * „Udział tematów" na zakładce Context — te same opcje ECharts, paleta i legenda.
 */
export const ReportTopicShare: React.FC<ReportTopicShareProps> = ({ topics, chartRef }) => {
  const { t } = useTranslation();

  // Kształt oczekiwany przez `prepareAndGetVerticalBarOptions`: nagłówek + wiersze [etykieta, wartość].
  const matrix = useMemo(
    () =>
      topics.length
        ? [['topic', 'value'], ...topics.map((entry) => [entry.topic, entry.share.toFixed(1)])]
        : [],
    [topics]
  );

  const labels = useMemo(() => matrix.map((row) => row[0]), [matrix]);
  const { legendStateMap, handleLegendClick } = useLegendState(labels);
  const legendData = useChartColorPalette(matrix, true);
  const colorMap = useMemo(
    () => new Map(legendData.map((item) => [item.label, item.color])),
    [legendData]
  );

  const maxYValue = useMemo(
    () => (topics.length ? Math.min(Math.ceil(Math.max(...topics.map((e) => e.share))), 100) : 100),
    [topics]
  );

  const chartOptions = useMemo(
    () =>
      prepareAndGetVerticalBarOptions(
        (params: any) => renderToStaticMarkup(<ChartTooltip tooltipFormatterParams={params} />),
        matrix,
        colorMap,
        maxYValue,
        // Raport pokazuje wartości nad słupkami — jego PDF jest zrzutem tego wykresu.
        true
      ),
    [matrix, colorMap, maxYValue]
  );

  const chartStyle = useMemo(
    () => ({ width: `${topics.length * BAR_WIDTH}px`, height: '235px', minWidth: '100%' }),
    [topics.length]
  );

  // Slajd bez danych w ogóle nie wchodzi do raportu — projekt może nie mieć jeszcze insightów.
  if (topics.length === 0) {
    return null;
  }

  return (
    <ReportSection
      title={t('reportTopicsTitle')}
      titleAccent={t('reportTopicsTitleAccent')}
      subtitle={t('reportTopicsSubtitle')}
    >
      <SimpleBar className="report-topics__scroll" style={{ maxWidth: '100%' }} autoHide={false}>
        <CustomBarChart
          chartRef={chartRef}
          customEChartsOption={{ eChartsOption: chartOptions, legendData }}
          legendStateMap={legendStateMap}
          onLegendClick={handleLegendClick}
          chartStyle={chartStyle}
        />
      </SimpleBar>
    </ReportSection>
  );
};

export default ReportTopicShare;
